import React, { useState, useEffect } from "react";
import { Bell, BellOff, BellRing, Smartphone, ShieldCheck, CheckCircle2, AlertTriangle, X, Send } from "lucide-react";
import { toast } from "sonner";
import {
    isPushSupported,
    isIosDevice,
    isStandalonePwa,
    getNotificationPermission,
    subscribeUserToPush,
    unsubscribeUserFromPush,
    sendTestPushNotification,
    checkPushStatus,
    isCurrentDeviceSubscribed,
} from "@/lib/pushNotifications";

export default function NotificationModal({ isOpen, onClose }) {
    const [loading, setLoading] = useState(false);
    const [testing, setTesting] = useState(false);
    const [permission, setPermission] = useState("default");
    const [deviceSubscribed, setDeviceSubscribed] = useState(false);
    const [serverStatus, setServerStatus] = useState({ subscribed: false, subscription_count: 0 });

    const isIos = isIosDevice();
    const isStandalone = isStandalonePwa();
    const supported = isPushSupported();

    const loadStatus = async () => {
        const perm = getNotificationPermission();
        setPermission(perm);
        const [status, isSubbed] = await Promise.all([
            checkPushStatus(),
            isCurrentDeviceSubscribed(),
        ]);
        setServerStatus(status);
        setDeviceSubscribed(isSubbed);

        // If browser permission is already granted but this device has not registered its subscription yet,
        // seamlessly register it now so that "Send Test Alert" or gate arrivals work right away!
        if (perm === "granted" && !isSubbed) {
            try {
                await subscribeUserToPush();
                setDeviceSubscribed(true);
                const updatedStatus = await checkPushStatus();
                setServerStatus(updatedStatus);
            } catch (err) {
                console.warn("Auto-register device subscription note:", err);
            }
        }
    };

    useEffect(() => {
        if (isOpen) {
            loadStatus();
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const handleEnable = async () => {
        if (isIos && !isStandalone) {
            toast.info("On iPhone, first tap Share 📤 and 'Add to Home Screen' to enable push notifications.");
            return;
        }

        setLoading(true);
        try {
            await subscribeUserToPush();
            setDeviceSubscribed(true);
            toast.success("Push notifications enabled! Sending a test alert...");
            await loadStatus();
            // Automatically send a welcome test push
            try {
                await sendTestPushNotification();
                toast.success("Welcome alert sent to your device! Check your notifications 🔔");
            } catch (e) {
                console.log("Test alert note:", e);
            }
        } catch (err) {
            toast.error(err.message || "Failed to enable notifications");
        } finally {
            setLoading(false);
        }
    };

    const handleDisable = async () => {
        setLoading(true);
        try {
            await unsubscribeUserFromPush();
            setDeviceSubscribed(false);
            toast.success("Device unsubscribed from push notifications.");
            await loadStatus();
        } catch (err) {
            toast.error("Failed to unsubscribe: " + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleTest = async () => {
        setTesting(true);
        try {
            // Guarantee this current device is registered before triggering the test push
            if (!deviceSubscribed) {
                await subscribeUserToPush();
                setDeviceSubscribed(true);
                await loadStatus();
            }
            const res = await sendTestPushNotification();
            if (res.ok) {
                toast.success(res.message || "Test notification sent! Check your screen 🔔");
            } else {
                toast.error(res.message || "Could not deliver notification.");
            }
        } catch (err) {
            toast.error(err.response?.data?.message || err.message || "Failed to trigger test alert");
        } finally {
            setTesting(false);
        }
    };

    const isEnabled = permission === "granted" && (deviceSubscribed || serverStatus.subscribed);

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-fade-in">
            <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl border border-nest-line overflow-hidden">
                {/* Header */}
                <div className="p-5 bg-nest-alt/60 border-b border-nest-line flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2.5 rounded-xl bg-nest-ink text-white">
                            <BellRing size={20} />
                        </div>
                        <div>
                            <h3 className="font-display font-semibold text-nest-ink text-base">Push Notifications</h3>
                            <p className="text-xs text-nest-ink3">Mobile & desktop visitor alerts</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1.5 rounded-lg text-nest-ink3 hover:text-nest-ink hover:bg-nest-alt transition"
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* Content */}
                <div className="p-5 space-y-4">
                    {/* iOS Notice if not installed */}
                    {isIos && !isStandalone && (
                        <div className="p-3.5 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-xs space-y-1.5">
                            <div className="flex items-center gap-2 font-semibold">
                                <AlertTriangle size={15} className="text-amber-600 shrink-0" />
                                <span>iPhone (iOS 16.4+) Requirement</span>
                            </div>
                            <p className="text-amber-800 leading-relaxed">
                                Apple requires NeighborNest to be added to your Home Screen to deliver lock-screen notifications.
                            </p>
                            <p className="font-medium text-amber-900">
                                1. Tap the Safari <strong>Share</strong> button (📤)<br />
                                2. Select <strong>"Add to Home Screen"</strong> (➕)<br />
                                3. Open the app from your Home Screen & tap Enable Notifications!
                            </p>
                        </div>
                    )}

                    {/* Permission Status Pill */}
                    <div className="flex items-center justify-between p-3.5 rounded-xl bg-nest-base border border-nest-line">
                        <div className="flex items-center gap-2.5">
                            {isEnabled ? (
                                <CheckCircle2 size={18} className="text-emerald-600" />
                            ) : permission === "denied" ? (
                                <AlertTriangle size={18} className="text-rose-600" />
                            ) : (
                                <Smartphone size={18} className="text-nest-ink3" />
                            )}
                            <div>
                                <p className="text-xs font-semibold text-nest-ink">
                                    {isEnabled
                                        ? "Notifications Active"
                                        : permission === "denied"
                                        ? "Notifications Blocked"
                                        : "Notifications Inactive"}
                                </p>
                                <p className="text-[11px] text-nest-ink3">
                                    {serverStatus.subscription_count > 0
                                        ? `${serverStatus.subscription_count} active device(s) registered`
                                        : "No devices registered yet"}
                                </p>
                            </div>
                        </div>
                        <span
                            className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                                isEnabled
                                    ? "bg-emerald-100 text-emerald-800"
                                    : permission === "denied"
                                    ? "bg-rose-100 text-rose-800"
                                    : "bg-nest-alt text-nest-ink3"
                            }`}
                        >
                            {isEnabled ? "Enabled" : permission === "denied" ? "Blocked" : "Disabled"}
                        </span>
                    </div>

                    {/* What you get */}
                    <div className="space-y-2 text-xs text-nest-ink2">
                        <p className="font-medium text-nest-ink">You will receive instant alerts for:</p>
                        <ul className="space-y-1.5 pl-1 text-[11px]">
                            <li className="flex items-center gap-2">
                                <ShieldCheck size={14} className="text-nest-clay shrink-0" />
                                <span><strong>Visitor Gate Check-ins:</strong> Alerts when guests or cabs arrive at the gate.</span>
                            </li>
                            <li className="flex items-center gap-2">
                                <ShieldCheck size={14} className="text-nest-clay shrink-0" />
                                <span><strong>Daily Help & Staff:</strong> Alerts when your cook, maid, or cleaner arrives.</span>
                            </li>
                            <li className="flex items-center gap-2">
                                <ShieldCheck size={14} className="text-nest-clay shrink-0" />
                                <span><strong>Society Notices:</strong> Emergency alerts & official announcements.</span>
                            </li>
                        </ul>
                    </div>

                    {permission === "denied" && (
                        <p className="text-xs text-rose-600 bg-rose-50 p-2.5 rounded-lg border border-rose-200">
                            Notifications are blocked by your browser settings. To enable, tap the lock/info icon in your address bar and set Notifications to "Allow".
                        </p>
                    )}
                </div>

                {/* Actions */}
                <div className="p-4 bg-nest-alt/40 border-t border-nest-line flex items-center justify-between gap-2">
                    {isEnabled ? (
                        <>
                            <button
                                onClick={handleDisable}
                                disabled={loading}
                                className="px-3 py-2 text-xs text-nest-ink3 hover:text-rose-600 transition flex items-center gap-1.5"
                            >
                                <BellOff size={14} />
                                <span>Unsubscribe</span>
                            </button>
                            <button
                                onClick={handleTest}
                                disabled={testing}
                                className="px-4 py-2 bg-nest-ink hover:bg-black text-white text-xs font-semibold rounded-xl flex items-center gap-1.5 transition shadow-sm"
                            >
                                <Send size={13} />
                                <span>{testing ? "Sending..." : "Send Test Alert"}</span>
                            </button>
                        </>
                    ) : (
                        <>
                            <button
                                onClick={onClose}
                                className="px-3.5 py-2 text-xs font-medium text-nest-ink3 hover:text-nest-ink transition"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleEnable}
                                disabled={loading || !supported}
                                className="px-4 py-2 bg-nest-clay hover:bg-[#8A5A4F] text-white text-xs font-semibold rounded-xl flex items-center gap-2 transition shadow-sm"
                            >
                                <Bell size={14} />
                                <span>{loading ? "Activating..." : "Enable Notifications"}</span>
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
